/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import Input from '@components/Input';
import { useValidateLicensePlateMutation } from '@shared/services/common';
import { useTranslation } from 'next-i18next';
import {
  mapLocaleCodeToCountry,
  mapLocaleCodeToPlate,
} from '@features/WordPress/utilities/helpers/locale';
import { useRouter } from 'next/router';
import classnames from 'classnames';

import { forwardRef, memo } from 'react';
import Button from '@components/Button';
import Link from '@components/Link';
import { useDispatch, useSelector } from 'react-redux';
import {
  Root,
  CountryLabel,
  Input as InputClass,
  InputHasError,
  WrapperClass,
} from './LicensePlate.module.css';
import { setErrorComponent } from '@features/Checkout/store';
import { LicensePlateErrors } from '@shared/types/common';

const LicensePlate = ({
  control,
  className,
  value,
  onBlur,
  name,
  customValidationRules,
  shouldNotValidate,
  showLoginLinkWhenAlreadyRegistered,
  membershipType = 'unlimited',
  ...props
}) => {
  const dispatch = useDispatch();
  const { locale } = useRouter();
  const { isSingle, errorComponent } = useSelector((state) => state.checkout);
  const [validateLicensePlate] = useValidateLicensePlateMutation();
  const { t } = useTranslation(['checkout', 'account', 'common']);
  const { loginButtonOnErrorMessage } = props;

  const LicensePlateAlreadyRegistered = () =>
    showLoginLinkWhenAlreadyRegistered ? (
      <div>
        {`${t('licensePlateAlreadyExists', { ns: 'common' })}. `}
        {!loginButtonOnErrorMessage ? (
          <Link href="/login">{t('login', { ns: 'common' })}</Link>
        ) : (
          <Button
            size="extra-small"
            appearance="text"
            onClick={() => loginButtonOnErrorMessage()}
          >
            {t('login', { ns: 'common' })}
          </Button>
        )}
        {` ${t('elsewhereContact', { ns: 'checkout' })} `}
        <Link href="/hjaelp" rel="noopener noreferrer" target="_blank">{`${t(
          'helpdesk'
        )}`}</Link>
        .
      </div>
    ) : (
      `${t('licensePlateAlreadyExists', { ns: 'common' })}.`
    );

  const LicensePlateIsTaxa = () => (
    <div>
      <span>{`${t('taxa', { ns: 'checkout' })} `}</span>
      <span>
        <Link
          href={`${process.env.NEXT_BASE_URL}${t(
            'readMoreSingleWashBusinessLink.path',
            { ns: 'checkout' }
          )}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t('readMoreSingleWashBusinessLink.text', { ns: 'checkout' })}
        </Link>
      </span>
    </div>
  );

  const handleErrorComponent = (component) => {
    dispatch(setErrorComponent(component));
  };

  // aparently on "DK" should have two letters, but other countries only one letter in license plate prefix
  const countryCodePrefix = mapLocaleCodeToCountry(locale);

  const InputField = forwardRef((inputProps, ref) => (
    <div className={Root}>
      <Input
        {...inputProps}
        addon={
          <div
            className={classnames(CountryLabel, {
              // eslint-disable-next-line react/destructuring-assignment
              [InputHasError]: inputProps.error,
            })}
          >
            {countryCodePrefix === 'dk'
              ? countryCodePrefix
              : countryCodePrefix.substr(0, 1)}
          </div>
        }
        inputWrapperClassName={WrapperClass}
        type="text"
        placeholder={mapLocaleCodeToPlate(locale).placeholder}
        className={classnames(InputClass, className)}
        country={mapLocaleCodeToCountry(locale)}
        showValidIcon
        showValidationErrorMessage={false}
        {...props}
        ref={ref}
      />
    </div>
  ));

  return !control ? (
    <InputField value={value} />
  ) : (
    <>
      <Controller
        control={control}
        rules={{
          required: {
            value: true,
            message: t('plateRequired', { ns: 'common' }),
          },
          validate: async (licensePlate) => {
            try {
              await validateLicensePlate({
                license_plate: licensePlate,
                membershipType: isSingle ? 'single' : membershipType,
              }).unwrap();
              handleErrorComponent(null);
              return true;
            } catch (error) {
              if (shouldNotValidate) return true;

              const response = error?.data?.response;
              if (response === LicensePlateErrors.ALREADY_EXISTS) {
                handleErrorComponent(<LicensePlateAlreadyRegistered />);
                return t('licensePlateAlreadyExists', { ns: 'common' });
              } else if (response === LicensePlateErrors.IS_TAXA) {
                handleErrorComponent(<LicensePlateIsTaxa />);
                return t('taxaError', { ns: 'common' });
              } else if (response === LicensePlateErrors.MISSING_VALUE) {
                handleErrorComponent(t('plateRequired', { ns: 'common' }));
                return t('plateRequired', { ns: 'common' });
              } else {
                handleErrorComponent(t(response, { ns: 'common' }));
                return t(response, { ns: 'common' });
              }
            }
          },
        }}
        name={name}
        value={value}
        render={({ field, fieldState }) => (
          <InputField
            {...field}
            onBlur={(event) => {
              field.onBlur(event);
              onBlur && onBlur(event.target.value);
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                e.target.blur();
              }
            }}
            value={field.value || ''}
            error={fieldState.error || Boolean(errorComponent && field.value)}
            underLabel={t('licensePlateUnderLabel', { ns: 'checkout' })}
            onChange={(event) =>
              field.onChange(
                event.target.value.toString().replace(/\s/g, '').toUpperCase()
              )
            }
          />
        )}
      />
    </>
  );
};

LicensePlate.defaultProps = {
  customValidationRules: {},
  showLoginLinkWhenAlreadyRegistered: true,
  shouldNotValidate: false,
};

LicensePlate.propTypes = {
  customValidationRules: PropTypes.object,
  showLoginLinkWhenAlreadyRegistered: PropTypes.bool,
  shouldNotValidate: PropTypes.bool,
};

export default memo(LicensePlate, () => true);
